'use client'

import { useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { convertParsedToFrameData, parseCIF, parsePDB } from '@/components/py2Dmol-ts-react/lib/parsing'
import { initializePy2DmolViewer } from '@/components/py2Dmol-ts-react/renderer/runtime'
import { Skeleton } from '@/components/ui/skeleton'

type PreviewStatus = 'loading' | 'ready' | 'error'

/**
 * Realistic concurrent WebGL budget for thumbnail generation.
 * Chrome/Edge often allow ~8–16 contexts/page; Safari is tighter.
 * We keep this low, snapshot to a PNG, then destroy WebGL so every card can finish.
 */
const MAX_ACTIVE_MINI_PREVIEWS = 4
let activeMiniPreviews = 0
const miniPreviewWaiters: Array<() => void> = []
const snapshotCache = new Map<string, string>()

function acquireMiniPreviewSlot() {
  if (activeMiniPreviews < MAX_ACTIVE_MINI_PREVIEWS) {
    activeMiniPreviews += 1
    return Promise.resolve()
  }
  return new Promise<void>((resolve) => {
    miniPreviewWaiters.push(() => {
      activeMiniPreviews += 1
      resolve()
    })
  })
}

function releaseMiniPreviewSlot() {
  activeMiniPreviews = Math.max(0, activeMiniPreviews - 1)
  const next = miniPreviewWaiters.shift()
  if (next) next()
}

function destroyViewer(viewerId: string) {
  try {
    if ((window as any).py2dmol_viewers?.[viewerId]) {
      delete (window as any).py2dmol_viewers[viewerId]
    }
  } catch {
    /* ignore */
  }
}

type Py2dMolMiniPreviewProps = {
  /** URL that returns mmCIF/PDB text (we use /api/structure?...). */
  url: string
  format: 'cif' | 'pdb'
  /** Unique-ish id for viewer registration; ok to reuse across renders for same card */
  viewerId: string
  /** When true, hide the in-viewer error text (parent can show a fallback icon). */
  hideError?: boolean
  /** Only generate when on screen; after snapshot, WebGL is released. */
  mountWhenVisible?: boolean
  onStatusChange?: (status: PreviewStatus) => void
}

export default function Py2dMolMiniPreview({
  url,
  format,
  viewerId,
  hideError = false,
  mountWhenVisible = false,
  onStatusChange,
}: Py2dMolMiniPreviewProps) {
  const searchParams = useSearchParams()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const cacheKey = `${viewerId}::${url}`
  const [snapshot, setSnapshot] = useState<string | null>(() => snapshotCache.get(cacheKey) ?? null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(!mountWhenVisible)
  const onStatusChangeRef = useRef(onStatusChange)
  onStatusChangeRef.current = onStatusChange

  const objectName = useMemo(() => `obj-${viewerId}`, [viewerId])
  const scopedUrl = useMemo(() => {
    try {
      const teamId = String(searchParams.get('team_id') || '').trim()
      if (!teamId) return url
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
      if (!parsed.searchParams.get('team_id')) {
        parsed.searchParams.set('team_id', teamId)
      }
      if (/^https?:\/\//i.test(url)) return parsed.toString()
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
      return url
    }
  }, [url, searchParams])

  // Cached snapshot → already ready.
  useEffect(() => {
    if (!snapshot) return
    onStatusChangeRef.current?.('ready')
  }, [snapshot])

  useEffect(() => {
    if (!mountWhenVisible) return
    const node = rootRef.current
    if (!node) return
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsVisible(Boolean(entry?.isIntersecting))
      },
      { rootMargin: '120px', threshold: 0.01 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [mountWhenVisible])

  useEffect(() => {
    // Static image already in hand — never hold a WebGL slot again.
    if (snapshot) return
    if (!isVisible) return

    let cancelled = false
    let slotHeld = false

    const report = (status: PreviewStatus) => {
      if (cancelled) return
      onStatusChangeRef.current?.(status)
    }

    const run = async () => {
      try {
        setError(null)
        setIsLoading(true)
        report('loading')

        await acquireMiniPreviewSlot()
        if (cancelled) {
          releaseMiniPreviewSlot()
          return
        }
        slotHeld = true

        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        if (cancelled) return

        const root = rootRef.current
        if (!root) throw new Error('Preview unavailable')

        ;(window as any).viewerConfig = {
          size: [300, 300],
          pae_size: [300, 300],
          color: 'auto',
          shadow: false,
          outline: false,
          width: 3.0,
          rotate: false,
          controls: false,
          autoplay: false,
          box: false,
          pastel: 0.15,
          pae: false,
          colorblind: false,
          viewer_id: viewerId,
          biounit: false,
          ignoreLigands: true,
          previewMode: true,
          interactive: false,
        }

        initializePy2DmolViewer(root)
        const api = (window as any).py2dmol_viewers?.[viewerId]
        if (!api) throw new Error('Failed to initialize preview')

        const res = await fetch(scopedUrl, { credentials: 'include' })
        if (!res.ok) throw new Error(`Failed to fetch structure (HTTP ${res.status})`)
        const text = await res.text()
        if (cancelled) return
        if (!text || text.trim().length === 0) throw new Error('Empty structure')

        const isCif = format === 'cif' || /^\s*data_/m.test(text) || /_atom_site\./.test(text)
        const models = isCif ? parseCIF(text) : parsePDB(text)
        const model0 = Array.isArray(models) && models.length > 0 ? models[0] : []
        if (!model0 || model0.length === 0) throw new Error('No atoms found')

        const frame = convertParsedToFrameData(model0)
        api.handlePythonClearAll()
        api.handlePythonNewObject(objectName)
        api.handlePythonUpdate(JSON.stringify(frame), objectName)

        // Let the GPU paint at least one frame before snapshotting.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        if (cancelled) return

        const canvas = root.querySelector('canvas') as HTMLCanvasElement | null
        if (!canvas || canvas.width < 2 || canvas.height < 2) {
          throw new Error('WebGL preview blank')
        }

        let dataUrl = ''
        try {
          dataUrl = canvas.toDataURL('image/png')
        } catch {
          throw new Error('Failed to snapshot preview')
        }
        if (!dataUrl || dataUrl.length < 32) throw new Error('Empty preview snapshot')

        snapshotCache.set(cacheKey, dataUrl)
        if (!cancelled) {
          setSnapshot(dataUrl)
          report('ready')
        }

        // Free the WebGL context immediately so the next cards can generate.
        destroyViewer(viewerId)
        if (slotHeld) {
          releaseMiniPreviewSlot()
          slotHeld = false
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to render preview')
          report('error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
        if (slotHeld) {
          releaseMiniPreviewSlot()
          slotHeld = false
        }
        destroyViewer(viewerId)
      }
    }

    void run()
    return () => {
      cancelled = true
      if (slotHeld) {
        releaseMiniPreviewSlot()
        slotHeld = false
      }
      destroyViewer(viewerId)
      // Only bounce parent back to icon if we never produced a snapshot.
      if (!snapshotCache.has(cacheKey)) {
        onStatusChangeRef.current?.('loading')
      }
    }
  }, [cacheKey, format, isVisible, objectName, scopedUrl, snapshot, viewerId])

  if (snapshot) {
    return (
      <div className="relative h-full w-full" data-viewer-root={viewerId}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={snapshot} alt="" className="h-full w-full object-contain" draggable={false} />
      </div>
    )
  }

  return (
    <div ref={rootRef} className="relative h-full w-full" data-viewer-root={viewerId}>
      <div id="canvasContainer" className="absolute inset-0">
        <canvas id="canvas" className="h-full w-full" />
      </div>

      {isLoading && !error && (
        <div className="pointer-events-none absolute inset-0 bg-background/40 p-3">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
      )}

      <div id="controlsContainer" className="hidden">
        <button id="playButton" />
        <input id="frameSlider" type="range" />
        <span id="frameCounter" />
        <select id="objectSelect" />
        <select id="speedSelect" />
        <input id="rotationCheckbox" type="checkbox" />
        <input id="lineWidthSlider" type="range" />
        <select id="colorSelect" />
        <input id="shadowEnabledCheckbox" type="checkbox" />
        <input id="outlineEnabledCheckbox" type="checkbox" />
        <input id="colorblindCheckbox" type="checkbox" />
      </div>
      <div id="paeContainer" className="hidden">
        <canvas id="paeCanvas" />
      </div>
      <div id="rightPanelContainer" className="hidden" />
      <div id="objectContainer" className="hidden" />

      {error && !hideError && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 text-[11px] text-muted-foreground">
          {error}
        </div>
      )}
    </div>
  )
}
